import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Tabs,
  Table,
  Button,
  Popconfirm,
  message,
  Spin,
  Tooltip,
  Popover,
  Tag,
  Dropdown,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  LinkOutlined,
  CopyOutlined,
  GlobalOutlined,
  HomeOutlined,
  UserOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import {
  ClientApartment,
  AptReservation,
  AptEvent,
  AptStats,
  AptPackage,
  AptIncome,
  AptExpense,
  getClientApartments,
  getAptReservations,
  getAptStats,
  getAptEvents,
  getAptPackages,
  getAptIncomes,
  getAptExpenses,
  deleteAptReservation,
  deleteClientApartment,
  deleteAptEvent,
  archiveAptEvent,
  updateAptReservationStatus,
  updateAptReservation,
  AptReservationStatus,
  ReservationExtra,
} from "../../utils/api/apartmentRentalsAPI";

import AddApartmentModal from "./Components/AddApartmentModal";
import AddReservationDrawer from "./Components/AddReservationDrawer";
import AddEventModal from "./Components/AddEventModal";
import StatsTab from "./Components/StatsTab";
import ReferralsTab from "./Components/ReferralsTab";
import FinanceTab from "./Components/FinanceTab";
import GuestsTab from "./Components/GuestsTab";
import PromoCodesTab from "./Components/PromoCodesTab";
import PackagesTab from "./Components/PackagesTab";
import MealsTab from "./Components/MealsTab";
import MenuTab from "./Components/MenuTab";
import MenuOrdersTab from "./Components/MenuOrdersTab";
import BlockedDatesTab from "./Components/BlockedDatesTab";
import CalculatorTab from "./Components/CalculatorTab";
import { isAdmin } from "../../utils/permissions";
import "./ApartmentReservations.scss";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const initials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();

const fmtRSD = (n: number) => new Intl.NumberFormat("sr-RS").format(n);

const fmtAmount = (n: number, currency?: string) => {
  if (currency === "EUR") return `€${new Intl.NumberFormat("de-DE").format(n)}`;
  if (currency === "BAM") return `${new Intl.NumberFormat("bs-BA").format(n)} KM`;
  return `${new Intl.NumberFormat("sr-RS").format(n)} RSD`;
};

const fmtDate = (d: string) => dayjs(d).format("DD.MM.YYYY");

const AVATAR_COLORS = [
  "#5b50d6", "#0e7490", "#ca8a04", "#16a34a",
  "#dc2626", "#7c3aed", "#0284c7", "#d97706",
];
const avatarColor = (i: number) => AVATAR_COLORS[i % AVATAR_COLORS.length];

const STATUS_LABEL: Record<AptReservationStatus, string> = {
  confirmed: "Potvrđena",
  pending: "Pending",
  staying: "U boravku",
  checkout: "Check-out danas",
  canceled: "Otkazana",
  noshow: "No-show",
};

const CHANNEL_DOT: Record<string, string> = {
  direct: "#16a34a",
  booking: "#5b50d6",
  airbnb: "#dc2626",
  phone: "#ca8a04",
  other: "#6b6880",
};

const CHANNEL_LABEL: Record<string, string> = {
  direct: "Direktno",
  booking: "Booking",
  airbnb: "Airbnb",
  phone: "Telefon",
  other: "Ostalo",
};

const MONTH_NAMES = [
  "Januar","Februar","Mart","April","Maj","Jun",
  "Jul","Avgust","Septembar","Oktobar","Novembar","Decembar",
];

// ─── Package (akcija) icons ────────────────────────────────────────────────────
// Packages have no structured "type" field — only free-text name/includes — so
// icons are inferred by keyword-matching against known package ingredients.
const PKG_ICON_RULES: { icon: string; keywords: string[] }[] = [
  { icon: "🍳", keywords: ["dorucak", "breakfast"] },
  { icon: "🍲", keywords: ["rucak", "lunch"] },
  { icon: "🍽️", keywords: ["vecera", "dinner"] },
  { icon: "🏍️", keywords: ["kvad", "quad", "atv", "buggy"] },
  { icon: "🚗", keywords: ["transfer", "prevoz", "prijevoz"] },
  { icon: "🗺️", keywords: ["tura", "izlet", "tour", "obilazak"] },
  { icon: "🍹", keywords: ["pice", "drink", "koktel", "bar"] },
  { icon: "🧖", keywords: ["spa", "masaza", "wellness", "sauna"] },
  { icon: "🅿️", keywords: ["parking"] },
  { icon: "🛎️", keywords: ["odjava", "checkout", "prijava", "checkin"] },
];

const normalizeForMatch = (s: string) =>
  s
    .toLowerCase()
    .replace(/[čć]/g, "c")
    .replace(/š/g, "s")
    .replace(/ž/g, "z")
    .replace(/đ/g, "dj");

const iconForText = (text: string): string | null => {
  const norm = normalizeForMatch(text);
  const rule = PKG_ICON_RULES.find((r) => r.keywords.some((kw) => norm.includes(kw)));
  return rule ? rule.icon : null;
};

// Resolves the small icon(s) shown on a calendar day for an attached package —
// one icon per matched ingredient (doručak/ručak/večera/kvad/...), deduped,
// falling back to a generic 🎁 gift icon when nothing matches.
function resolvePackageIcons(pkg: AptPackage | undefined, fallbackName: string): string[] {
  const icons: string[] = [];
  if (pkg?.includes?.length) {
    for (const inc of pkg.includes) {
      const icon = inc.icon || iconForText(inc.label || "");
      if (icon && !icons.includes(icon)) icons.push(icon);
    }
  }
  if (!icons.length) {
    const icon = iconForText(pkg?.name || fallbackName || "");
    icons.push(icon || "🎁");
  }
  return icons;
}

// Renders a day cell's content: the day number, or — when a package is
// attached — its icon(s) shrunk to fit a 2x2 grid, capped with a "+N" badge
// (taking one of the 4 slots) so the grid never overflows the tiny cell.
// The day number is also kept, as a small corner chip, so the exact date
// stays legible instead of getting fully replaced by the icon — a centered
// watermark behind the icon doesn't work for a single icon since it's
// opaque and roughly the same size, so it just hides the number underneath.
const PKG_ICONS_GRID = 4;
function renderPkgLabel(icons: string[] | undefined, day: number): React.ReactNode {
  if (!icons || !icons.length) return day;
  const iconNode =
    icons.length === 1 ? (
      <span className="ar-apt-cal-day__pkg-dot">{icons[0]}</span>
    ) : (
      (() => {
        const overflowing = icons.length > PKG_ICONS_GRID;
        const shown = overflowing ? icons.slice(0, PKG_ICONS_GRID - 1) : icons;
        const extra = overflowing ? icons.length - shown.length : 0;
        return (
          <span className="ar-apt-cal-day__pkg-icons">
            {shown.map((ic, idx) => (
              <span key={idx} className="ar-apt-cal-day__pkg-icon">{ic}</span>
            ))}
            {extra > 0 && <span className="ar-apt-cal-day__pkg-icon ar-apt-cal-day__pkg-icon--more">+{extra}</span>}
          </span>
        );
      })()
    );
  return (
    <>
      {iconNode}
      <span className="ar-apt-cal-day__pkg-date">{day}</span>
    </>
  );
}

// ─── Mini Calendar ────────────────────────────────────────────────────────────

interface DayStatus {
  status: "confirmed" | "pending" | "free" | "event" | "blocked";
}

function buildDayMap(
  reservations: AptReservation[],
  aptId: string,
  year: number,
  month: number,
  events: AptEvent[] = [],
  blockedPeriods: { from: string; to: string }[] = [],
  packages: AptPackage[] = []
): { dayMap: Record<number, DayStatus["status"]>; packageDays: Record<number, string>; packageIcons: Record<number, string[]>; dayReservation: Record<number, AptReservation>; checkInDays: Record<number, boolean>; checkOutDays: Record<number, boolean>; checkOutReservation: Record<number, AptReservation> } {
  const map: Record<number, DayStatus["status"]> = {};
  const packageDays: Record<number, string> = {};
  const packageIcons: Record<number, string[]> = {};
  const dayReservation: Record<number, AptReservation> = {};
  const checkInDays: Record<number, boolean> = {};
  const checkOutDays: Record<number, boolean> = {};
  const checkOutReservation: Record<number, AptReservation> = {};
  const daysInMonth = new Date(year, month, 0).getDate();

  reservations
    .filter((r) => {
      const rAptId =
        typeof r.apartmentId === "object"
          ? (r.apartmentId as any)._id
          : r.apartmentId;
      return (
        rAptId === aptId &&
        r.status !== "canceled" &&
        r.status !== "noshow"
      );
    })
    .forEach((r) => {
      const ci = dayjs(r.checkIn.substring(0, 10));
      const co = dayjs(r.checkOut.substring(0, 10));

      if (ci.year() === year && ci.month() === month - 1) {
        checkInDays[ci.date()] = true;
      }
      if (co.year() === year && co.month() === month - 1) {
        checkOutDays[co.date()] = true;
        checkOutReservation[co.date()] = r;
      }

      for (let d = 1; d <= daysInMonth; d++) {
        const day = dayjs(new Date(year, month - 1, d));
        if (
          (day.isAfter(ci.subtract(1, "day")) || day.isSame(ci, "day")) &&
          day.isBefore(co)
        ) {
          const s =
            r.type === "blogger" || r.type === "discount" || r.type === "private"
              ? "event"
              : r.status === "pending"
              ? "pending"
              : "confirmed";
          if (!map[d] || map[d] === "free") map[d] = s;
          if (r.packageName && !packageDays[d]) {
            packageDays[d] = r.packageName;
            const pkg = r.packageId ? packages.find((p) => p._id === r.packageId) : undefined;
            packageIcons[d] = resolvePackageIcons(pkg, r.packageName);
          }
          if (!dayReservation[d]) dayReservation[d] = r;
        }
      }
    });

  events
    .filter((ev) => {
      if (!ev.startDate || !ev.endDate) return false;
      const evAptId =
        typeof ev.apartmentId === "object"
          ? (ev.apartmentId as any)?._id
          : ev.apartmentId;
      return !evAptId || evAptId === aptId;
    })
    .forEach((ev) => {
      const start = dayjs(ev.startDate!.substring(0, 10));
      const end   = dayjs(ev.endDate!.substring(0, 10));
      for (let d = 1; d <= daysInMonth; d++) {
        const day = dayjs(new Date(year, month - 1, d));
        if ((day.isSame(start) || day.isAfter(start)) && day.isBefore(end)) {
          if (!map[d] || map[d] === "free") map[d] = "event";
        }
      }
    });

  blockedPeriods.forEach((bp) => {
    const from = dayjs(bp.from);
    const to = dayjs(bp.to);
    for (let d = 1; d <= daysInMonth; d++) {
      const day = dayjs(new Date(year, month - 1, d));
      if ((day.isSame(from) || day.isAfter(from)) && (day.isSame(to) || day.isBefore(to))) {
        if (!map[d] || map[d] === "free") map[d] = "blocked";
      }
    }
  });

  return { dayMap: map, packageDays, packageIcons, dayReservation, checkInDays, checkOutDays, checkOutReservation };
}

// A day cell where one guest checks out and another checks in — lets the
// user pick which of the two reservations to open instead of guessing.
interface TurnoverDayCellProps {
  cls: string;
  day: number;
  label: React.ReactNode;
  inRes: AptReservation;
  outRes: AptReservation;
  onSelect: (day: number, res: AptReservation) => void;
}

const TurnoverDayCell: React.FC<TurnoverDayCellProps> = ({ cls, day, label, inRes, outRes, onSelect }) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="top"
      overlayClassName="ar-apt-cal-turnover-pop"
      content={
        <div className="ar-apt-cal-turnover-menu">
          <button
            type="button"
            className="ar-apt-cal-turnover-opt ar-apt-cal-turnover-opt--out"
            onClick={() => {
              setOpen(false);
              onSelect(day, outRes);
            }}
          >
            <span className="ar-apt-cal-turnover-dot ar-apt-cal-turnover-dot--out" />
            <span className="ar-apt-cal-turnover-txt">
              <span className="ar-apt-cal-turnover-name">{outRes.guestName}</span>
              <span className="ar-apt-cal-turnover-sub">Odlazi danas</span>
            </span>
          </button>
          <button
            type="button"
            className="ar-apt-cal-turnover-opt ar-apt-cal-turnover-opt--in"
            onClick={() => {
              setOpen(false);
              onSelect(day, inRes);
            }}
          >
            <span className="ar-apt-cal-turnover-dot ar-apt-cal-turnover-dot--in" />
            <span className="ar-apt-cal-turnover-txt">
              <span className="ar-apt-cal-turnover-name">{inRes.guestName}</span>
              <span className="ar-apt-cal-turnover-sub">Dolazi danas</span>
            </span>
          </button>
        </div>
      }
    >
      <div
        className={cls}
        style={{ cursor: "pointer" }}
        title={`${outRes.guestName} odlazi · ${inRes.guestName} dolazi`}
      >
        {label}
      </div>
    </Popover>
  );
};

interface MiniCalProps {
  year: number;
  month: number; // 1-based
  dayMap: Record<number, DayStatus["status"]>;
  packageDays?: Record<number, string>;
  packageIcons?: Record<number, string[]>;
  dayReservation?: Record<number, AptReservation>;
  checkInDays?: Record<number, boolean>;
  checkOutDays?: Record<number, boolean>;
  checkOutReservation?: Record<number, AptReservation>;
  onDayClick?: (day: number, res?: AptReservation) => void;
}

const MiniCal: React.FC<MiniCalProps> = ({ year, month, dayMap, packageDays = {}, packageIcons = {}, dayReservation = {}, checkInDays = {}, checkOutDays = {}, checkOutReservation = {}, onDayClick }) => {
  const dows = ["P", "U", "S", "Č", "P", "S", "N"];
  const today = dayjs();
  const firstDow = new Date(year, month - 1, 1).getDay();
  const leading = (firstDow + 6) % 7;
  const daysInMonth = new Date(year, month, 0).getDate();

  const cells: React.ReactNode[] = dows.map((d, i) => (
    <div key={`dow-${i}`} className="ar-apt-cal-dow">{d}</div>
  ));

  for (let i = 0; i < leading; i++) {
    const prevDay = new Date(year, month - 1, 0).getDate() - leading + i + 1;
    cells.push(
      <div key={`prev-${i}`} className="ar-apt-cal-day ar-apt-cal-day--muted">
        {prevDay}
      </div>
    );
  }

  for (let d = 1; d <= daysInMonth; d++) {
    const isToday =
      today.year() === year &&
      today.month() + 1 === month &&
      today.date() === d;
    const status = dayMap[d] || "free";
    const pkgName = packageDays[d];
    const pkgIcons = packageIcons[d];
    const isCheckIn = !!checkInDays[d];
    const isCheckOut = !!checkOutDays[d];
    const cls = [
      "ar-apt-cal-day",
      pkgName && "ar-apt-cal-day--package",
      !pkgName && status === "confirmed" && "ar-apt-cal-day--confirmed",
      !pkgName && status === "pending" && "ar-apt-cal-day--pending",
      status === "event" && "ar-apt-cal-day--event",
      status === "blocked" && "ar-apt-cal-day--blocked",
      isToday && "ar-apt-cal-day--today",
      isCheckIn && "ar-apt-cal-day--checkin",
      isCheckOut && "ar-apt-cal-day--checkout",
    ]
      .filter(Boolean)
      .join(" ");

    const isBooked = status === "confirmed" || status === "pending";
    const dayRes = dayReservation[d];

    let tooltipTitle: React.ReactNode;
    if (dayRes) {
      const ci = dayjs(dayRes.checkIn.substring(0, 10));
      const co = dayjs(dayRes.checkOut.substring(0, 10));
      const nights = Math.max(1, co.diff(ci, "day"));
      tooltipTitle = (
        <div>
          <div style={{ fontWeight: 600 }}>{pkgName ? `${(pkgIcons || ["🎁"]).join(" ")} ${pkgName}` : dayRes.guestName}</div>
          <div style={{ fontSize: 12, opacity: 0.85 }}>
            {ci.format("DD.MM")} → {co.format("DD.MM")} · {nights} {nights === 1 ? "noćenje" : "noćenja"}
          </div>
          {isCheckIn && <div style={{ fontSize: 11, color: "#22c55e", fontWeight: 600 }}>↳ Dolazak danas</div>}
          {isCheckOut && (
            <div style={{ fontSize: 11, color: "#ef4444", fontWeight: 600 }}>
              ↳ {checkOutReservation[d] && checkOutReservation[d]._id !== dayRes._id ? `${checkOutReservation[d].guestName} odlazi danas` : "Odlazak danas"}
            </div>
          )}
        </div>
      );
    } else if (isCheckOut) {
      const outRes = checkOutReservation[d];
      tooltipTitle = outRes ? (
        <div>
          <div style={{ fontWeight: 600 }}>{outRes.guestName}</div>
          <div style={{ fontSize: 12, color: "#ef4444", fontWeight: 600 }}>↳ Odlazak danas</div>
        </div>
      ) : "Odlazak gosta danas";
    } else if (status === "blocked") {
      tooltipTitle = "Blokirano";
    } else if (isBooked) {
      tooltipTitle = "Otvori rezervaciju";
    }

    const isClickable = isBooked || (isCheckOut && !!checkOutReservation[d]);
    const outRes = checkOutReservation[d];
    const isTurnover = isCheckIn && isCheckOut && !!dayRes && !!outRes && dayRes._id !== outRes._id;
    const dayLabel = renderPkgLabel(pkgIcons, d);

    if (isTurnover && onDayClick) {
      cells.push(
        <TurnoverDayCell
          key={`d-${d}`}
          cls={cls}
          day={d}
          label={dayLabel}
          inRes={dayRes as AptReservation}
          outRes={outRes as AptReservation}
          onSelect={onDayClick}
        />
      );
      continue;
    }

    cells.push(
      <Tooltip key={`d-${d}`} title={tooltipTitle} mouseEnterDelay={0.3}>
        <div
          className={cls}
          style={isClickable && onDayClick ? { cursor: "pointer" } : undefined}
          onClick={isClickable && onDayClick ? () => onDayClick(d) : undefined}
        >
          {dayLabel}
        </div>
      </Tooltip>
    );
  }

  return <div className="ar-apt-cal-grid">{cells}</div>;
};

// ─── Apartment Card ────────────────────────────────────────────────────────────

interface AptCardProps {
  apt: ClientApartment;
  reservations: AptReservation[];
  events: AptEvent[];
  aptIncomes: AptIncome[];
  aptExpenses: AptExpense[];
  packages: AptPackage[];
  year: number;
  month: number;
  onEdit: (apt: ClientApartment) => void;
  onDelete: (id: string) => void;
  onNewReservation: (aptId: string) => void;
  onEditReservation: (r: AptReservation) => void;
  index: number;
}

const AptCard: React.FC<AptCardProps> = ({
  apt,
  reservations,
  events,
  aptIncomes,
  aptExpenses,
  packages,
  year,
  month,
  onEdit,
  onDelete,
  onNewReservation,
  onEditReservation,
  index,
}) => {
  const aptReservations = reservations.filter((r) => {
    const rAptId =
      typeof r.apartmentId === "object"
        ? (r.apartmentId as any)._id
        : r.apartmentId;
    return rAptId === apt._id && r.status !== "canceled" && r.status !== "noshow";
  });

  const daysInMonth = new Date(year, month, 0).getDate();
  const monthStart     = dayjs(new Date(year, month - 1, 1));
  const nextMonthStart = dayjs(new Date(year, month, 1)); // first day of next month

  // Prorate each reservation: count only the nights that fall within this month
  const prorateNights = (r: AptReservation) => {
    const ci = dayjs(r.checkIn.substring(0, 10));
    const co = dayjs(r.checkOut.substring(0, 10));
    const resNights = Math.max(1, co.diff(ci, "day"));
    const start = ci.isBefore(monthStart) ? monthStart : ci;
    const end   = co.isAfter(nextMonthStart) ? nextMonthStart : co;
    const nightsInMonth = Math.max(0, end.diff(start, "day"));
    return { nightsInMonth, resNights, ratio: nightsInMonth / resNights };
  };

  const occupiedNights = aptReservations.reduce((sum, r) => {
    return sum + prorateNights(r).nightsInMonth;
  }, 0);

  const occupancy = Math.round((occupiedNights / daysInMonth) * 100);

  const reservationRevenue = aptReservations.reduce((s, r) => {
    return s + (r.amount || 0) * prorateNights(r).ratio;
  }, 0);

  // Only entries explicitly tagged to this apartment, in its own currency — avoids mixing currencies into one number
  const isForThisApt = (x: { apartmentId?: string | { _id: string } | null; currency: string }) => {
    const xAptId = typeof x.apartmentId === "object" ? x.apartmentId?._id : x.apartmentId;
    return xAptId === apt._id && x.currency === apt.currency;
  };
  const additionalIncome = aptIncomes.filter(isForThisApt).reduce((s, i) => s + (i.amount || 0), 0);
  const additionalExpense = aptExpenses.filter(isForThisApt).reduce((s, e) => s + (e.amount || 0), 0);
  const revenue = reservationRevenue + additionalIncome - additionalExpense;

  const { dayMap, packageDays, packageIcons, dayReservation, checkInDays, checkOutDays, checkOutReservation } = buildDayMap(reservations, apt._id, year, month, events, apt.blockedPeriods || [], packages);

  const handleDayClick = (day: number, explicitRes?: AptReservation) => {
    if (explicitRes) {
      onEditReservation(explicitRes);
      return;
    }
    const clicked = dayjs(new Date(year, month - 1, day));
    const res = aptReservations.find((r) => {
      const ci = dayjs(r.checkIn.substring(0, 10));
      const co = dayjs(r.checkOut.substring(0, 10));
      return (clicked.isSame(ci) || clicked.isAfter(ci)) && clicked.isBefore(co);
    }) || checkOutReservation[day];
    if (res) onEditReservation(res);
  };

  const isLive = aptReservations.some((r) => r.status === "staying");

  // Next upcoming reservation
  const upcoming = reservations
    .filter((r) => {
      const rAptId =
        typeof r.apartmentId === "object"
          ? (r.apartmentId as any)._id
          : r.apartmentId;
      return (
        rAptId === apt._id &&
        r.status !== "canceled" &&
        r.status !== "noshow" &&
        dayjs(r.checkIn).isAfter(dayjs())
      );
    })
    .sort((a, b) => dayjs(a.checkIn).diff(dayjs(b.checkIn)))[0];

  const coverStyle = apt.coverGradient
    ? { background: apt.coverGradient }
    : { background: `linear-gradient(135deg, ${avatarColor(index)} 0%, ${avatarColor(index + 3)} 100%)` };

  return (
    <div className="ar-apt-card">
      <div className="ar-apt-cover" style={coverStyle}>
        <div className="overlay" />
        <div className="ar-apt-cover-tags">
          {apt.tags?.slice(0, 2).map((tag) => (
            <span key={tag} className="ar-apt-tag">{tag}</span>
          ))}
          {isLive && (
            <span className="ar-apt-tag ar-apt-tag--live">GOST U BORAVKU</span>
          )}
        </div>
        <div className="ar-apt-cover-actions">
          <Dropdown
            menu={{
              items: [
                {
                  key: "new-res",
                  label: "Nova rezervacija",
                  icon: <PlusOutlined />,
                  onClick: () => onNewReservation(apt._id),
                },
                {
                  key: "copy-url",
                  label: "Kopiraj javni URL",
                  icon: <LinkOutlined />,
                  onClick: () => {
                    const url = `${window.location.origin}/apartments/public/${apt._id}?clientId=${
                      typeof apt.clientId === "object" ? (apt.clientId as any)._id : apt.clientId
                    }`;
                    navigator.clipboard.writeText(url);
                  },
                },
                {
                  key: "edit",
                  label: "Izmijeni",
                  icon: <EditOutlined />,
                  onClick: () => onEdit(apt),
                },
                {
                  key: "delete",
                  label: "Izbriši",
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => onDelete(apt._id),
                },
              ],
            }}
            trigger={["click"]}
          >
            <button className="ar-apt-cover-btn">
              <MoreOutlined />
            </button>
          </Dropdown>
        </div>
        <div className="ar-apt-cover-name">
          <div className="nm">{apt.name}</div>
          <div className="meta">
            <span>📍 {apt.location}</span>
            <span>👤 {apt.capacity} gosta</span>
          </div>
        </div>
      </div>

      <div className="ar-apt-stats">
        <div className="ar-apt-stat">
          <div className="lab">Popunj.</div>
          <div className="val">
            {occupancy}
            <span className="unit">%</span>
          </div>
        </div>
        <div className="ar-apt-stat">
          <div className="lab">Rezerv.</div>
          <div className="val">{aptReservations.length}</div>
        </div>
        <div
          className="ar-apt-stat"
          title={
            additionalIncome || additionalExpense
              ? `Rezervacije: ${fmtAmount(reservationRevenue, apt.currency)} + dodatni prihod: ${fmtAmount(additionalIncome, apt.currency)} − rashodi: ${fmtAmount(additionalExpense, apt.currency)}`
              : undefined
          }
        >
          <div className="lab">Prihod</div>
          <div className="val" style={{ fontSize: 13 }}>
            {fmtAmount(revenue, apt.currency)}
          </div>
        </div>
      </div>

      <div className="ar-apt-cal">
        <div className="ar-apt-cal-head">
          <div className="ar-apt-cal-month">
            {MONTH_NAMES[month - 1]} {year}
          </div>
          <div className="ar-apt-cal-legend">
            <span>
              <i style={{ background: "rgba(91,80,214,0.25)" }} />
              Rezervisan
            </span>
            <span>
              <i style={{ background: "#fef9c3" }} />
              Pending
            </span>
            <span>
              <i style={{ background: "#f3e8ff" }} />
              Event
            </span>
            <span>
              <i style={{ background: "#16a34a" }} />
              Dolazak
            </span>
            <span>
              <i style={{ background: "#dc2626" }} />
              Odlazak
            </span>
          </div>
        </div>
        <MiniCal
          year={year}
          month={month}
          dayMap={dayMap}
          packageDays={packageDays}
          packageIcons={packageIcons}
          dayReservation={dayReservation}
          checkInDays={checkInDays}
          checkOutDays={checkOutDays}
          checkOutReservation={checkOutReservation}
          onDayClick={handleDayClick}
        />
      </div>

      <div className="ar-apt-foot">
        <div className="ar-apt-next">
          <div className="ar-apt-next-lab">Sljedeći dolazak</div>
          {upcoming ? (
            <div className="ar-apt-next-val">
              <span
                className="ar-guest-ava"
                style={{ background: avatarColor(index) }}
              >
                {initials(upcoming.guestName)}
              </span>
              <span className="nm">{upcoming.guestName}</span>
              <span className="when">· {fmtDate(upcoming.checkIn)}</span>
            </div>
          ) : (
            <div className="ar-apt-next-val" style={{ color: "#9893a8" }}>
              Nema nadolazećih
            </div>
          )}
        </div>
        <button
          className="ar-btn ar-btn--ghost"
          onClick={() => onNewReservation(apt._id)}
        >
          + Rezervacija
        </button>
      </div>
    </div>
  );
};

// ─── Events Grid ──────────────────────────────────────────────────────────────

// ─── Event helpers ─────────────────────────────────────────────────────────────

type EventState = "active" | "expired_recent" | "expired_old" | "archived";

const GRACE_DAYS = 3; // show expired events for 3 days before auto-hiding

function getEventState(ev: AptEvent): EventState {
  if (ev.archived) return "archived";
  if (!ev.endDate) return "active";
  const end  = dayjs(ev.endDate.substring(0, 10));
  const now  = dayjs().startOf("day");
  if (end.isAfter(now) || end.isSame(now)) return "active";
  const daysAgo = now.diff(end, "day");
  return daysAgo <= GRACE_DAYS ? "expired_recent" : "expired_old";
}

interface EventsGridProps {
  events: AptEvent[];
  onEdit: (ev: AptEvent) => void;
  onDelete: (id: string) => void;
  onArchive: (id: string, archived: boolean) => void;
}

const EventsGrid: React.FC<EventsGridProps> = ({ events, onEdit, onDelete, onArchive }) => {
  const [showExpired, setShowExpired]   = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const categorized = useMemo(() => {
    const active: AptEvent[]         = [];
    const expiredRecent: AptEvent[]  = [];
    const expiredOld: AptEvent[]     = [];
    const archived: AptEvent[]       = [];
    events.forEach((ev) => {
      const s = getEventState(ev);
      if (s === "active")          active.push(ev);
      else if (s === "expired_recent") expiredRecent.push(ev);
      else if (s === "expired_old")    expiredOld.push(ev);
      else                             archived.push(ev);
    });
    return { active, expiredRecent, expiredOld, archived };
  }, [events]);

  const visible = useMemo(() => {
    const list = [...categorized.active, ...categorized.expiredRecent];
    if (showExpired)  list.push(...categorized.expiredOld);
    if (showArchived) list.push(...categorized.archived);
    return list;
  }, [categorized, showExpired, showArchived]);

  if (events.length === 0) {
    return (
      <div className="ar-empty">
        <div style={{ fontSize: 42, opacity: 0.4 }}>📅</div>
        <div className="t">Nema događaja</div>
        <div className="s">Bloger posjete, akcije i popusti biće prikazani ovdje</div>
      </div>
    );
  }

  return (
    <div>
      {/* Toggle pills */}
      {(categorized.expiredOld.length > 0 || categorized.archived.length > 0) && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          {categorized.expiredOld.length > 0 && (
            <button
              className={`ar-ev-toggle-btn${showExpired ? " active" : ""}`}
              onClick={() => setShowExpired((v) => !v)}
            >
              {showExpired ? "▾" : "▸"} Istekli ({categorized.expiredOld.length})
            </button>
          )}
          {categorized.archived.length > 0 && (
            <button
              className={`ar-ev-toggle-btn ar-ev-toggle-btn--arch${showArchived ? " active" : ""}`}
              onClick={() => setShowArchived((v) => !v)}
            >
              {showArchived ? "▾" : "▸"} Arhivirani ({categorized.archived.length})
            </button>
          )}
        </div>
      )}

      {visible.length === 0 ? (
        <div className="ar-empty" style={{ padding: "40px 0" }}>
          <div className="t">Nema aktivnih događaja</div>
          <div className="s">Svi događaji su istekli ili arhivirani</div>
        </div>
      ) : (
        <div className="ar-events-grid">
          {visible.map((ev) => {
            const isBlog  = ev.type === "blogger";
            const state   = getEventState(ev);
            const expired = state === "expired_recent" || state === "expired_old";
            const isArch  = state === "archived";

            return (
              <div
                key={ev._id}
                className={[
                  "ar-ev-card",
                  `ar-ev-card--${ev.type}`,
                  expired && "ar-ev-card--expired",
                  isArch && "ar-ev-card--archived",
                ].filter(Boolean).join(" ")}
              >
                <div className="ar-ev-head">
                  <span className={`ar-ev-ic ar-ev-ic--${ev.type}`}>
                    {isBlog ? "👤" : "🏷"}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <div className={`ar-ev-eyebrow ar-ev-eyebrow--${ev.type}`}>
                        {isBlog ? "Bloger / Influencer" : "Akcija / Popust"}
                      </div>
                      {expired && (
                        <span className="ar-ev-badge ar-ev-badge--expired">Isteklo</span>
                      )}
                      {isArch && (
                        <span className="ar-ev-badge ar-ev-badge--archived">Arhivirano</span>
                      )}
                    </div>
                    <div className="ar-ev-title">{ev.title}</div>
                    {ev.sub && <div className="ar-ev-sub">{ev.sub}</div>}
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    {!isArch && (
                      <Tooltip title="Izmijeni">
                        <button
                          className="ar-apt-cover-btn"
                          style={{ background: "#fff", border: "1px solid #e8e5f0" }}
                          onClick={() => onEdit(ev)}
                        >
                          <EditOutlined style={{ fontSize: 12 }} />
                        </button>
                      </Tooltip>
                    )}
                    <Tooltip title={isArch ? "Vrati iz arhive" : "Arhiviraj"}>
                      <button
                        className="ar-apt-cover-btn"
                        style={{
                          background: isArch ? "#ede9fd" : "#fff",
                          border: `1px solid ${isArch ? "rgba(91,80,214,0.3)" : "#e8e5f0"}`,
                          color: isArch ? "#5b50d6" : "#6b6880",
                        }}
                        onClick={() => onArchive(ev._id, !isArch)}
                      >
                        <span style={{ fontSize: 11, fontWeight: 700 }}>{isArch ? "↩" : "⬚"}</span>
                      </button>
                    </Tooltip>
                    <Popconfirm
                      title="Izbriši događaj?"
                      onConfirm={() => onDelete(ev._id)}
                      okText="Da"
                      cancelText="Ne"
                    >
                      <Tooltip title="Izbriši">
                        <button
                          className="ar-apt-cover-btn"
                          style={{ background: "#fff", border: "1px solid #e8e5f0" }}
                        >
                          <DeleteOutlined style={{ fontSize: 12 }} />
                        </button>
                      </Tooltip>
                    </Popconfirm>
                  </div>
                </div>

                <div className="ar-ev-meta">
                  <div className="ar-ev-meta-row">
                    <span className="ar-ev-meta-lab">Apartman</span>
                    <span className="ar-ev-meta-val">
                      {ev.apartmentName || "Svi apartmani"}
                    </span>
                  </div>
                  <div className="ar-ev-meta-row">
                    <span className="ar-ev-meta-lab">Period</span>
                    <span className="ar-ev-meta-val">
                      {ev.startDate
                        ? `${fmtDate(ev.startDate)} – ${ev.endDate ? fmtDate(ev.endDate) : "..."}`
                        : "Trajno"}
                    </span>
                  </div>
                  {ev.nights > 0 && (
                    <div className="ar-ev-meta-row">
                      <span className="ar-ev-meta-lab">Noći</span>
                      <span className="ar-ev-meta-val">{ev.nights}</span>
                    </div>
                  )}
                  {ev.deliverables && (
                    <div className="ar-ev-meta-row">
                      <span className="ar-ev-meta-lab">
                        {isBlog ? "Deliverables" : "Iskorišteno"}
                      </span>
                      <span className="ar-ev-meta-val">{ev.deliverables}</span>
                    </div>
                  )}
                </div>

                {ev.extras?.length > 0 && (
                  <div className="ar-ev-extras">
                    {ev.extras.map((e, i) => (
                      <span
                        key={i}
                        className={`ar-ev-extra${e.cls ? ` ar-ev-extra--${e.cls}` : ""}`}
                      >
                        {e.label}
                      </span>
                    ))}
                  </div>
                )}

                <div className="ar-ev-foot">
                  <div className="ar-ev-cost">
                    <span className="ar-ev-cost-lab">
                      {isBlog ? "Smještaj" : "Cijena"}
                    </span>
                    <span className={`ar-ev-cost-val${ev.cost === 0 ? " ar-ev-cost-val--free" : ""}`}>
                      {ev.cost === 0 ? (isBlog ? "Free" : "—") : fmtAmount(ev.cost, ev.currency)}
                    </span>
                  </div>
                  {ev.extraCost > 0 && (
                    <div className="ar-ev-cost">
                      <span className="ar-ev-cost-lab">Ekstra</span>
                      <span className="ar-ev-cost-val">
                        {fmtAmount(ev.extraCost, ev.currency)}
                      </span>
                    </div>
                  )}
                  {(ev.guestPayment ?? 0) > 0 && (
                    <div className="ar-ev-cost ar-ev-cost--payout">
                      <span className="ar-ev-cost-lab">Naknada gostu</span>
                      <span className="ar-ev-cost-val ar-ev-cost-val--payout">
                        {fmtAmount(ev.guestPayment!, ev.currency)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Mobile Reservation Card ──────────────────────────────────────────────────

interface ResMobileCardProps {
  r: AptReservation;
  apt: ClientApartment | undefined;
  index: number;
  onEdit: () => void;
  onDelete: () => void;
  onStatusChange: (id: string, status: AptReservationStatus) => void;
  onMarkPaid: (id: string) => void;
  onAddDeposit: (id: string, amount: number) => void;
}

const ResMobileCard: React.FC<ResMobileCardProps> = ({
  r, apt, index, onEdit, onDelete, onStatusChange, onMarkPaid, onAddDeposit,
}) => {
  const [depositOpen, setDepositOpen] = useState(false);
  const [depositVal, setDepositVal] = useState("");

  const nights = dayjs(r.checkOut).diff(dayjs(r.checkIn), "day");
  const ps = r.paymentStatus || (r.isPaid ? "paid" : "unpaid");
  const paid = r.paidAmount ?? (r.isPaid ? r.amount : 0);
  const remaining = Math.max(0, (r.amount || 0) - paid);
  const currency = apt?.currency;

  const nextStatus: { label: string; value: AptReservationStatus } | null =
    r.status === "pending"   ? { label: "✓ Potvrdi",    value: "confirmed" } :
    r.status === "confirmed" ? { label: "🏠 U boravku", value: "staying"   } :
    r.status === "staying"   ? { label: "🚪 Check-out", value: "checkout"  } :
    null;

  const handleDeposit = () => {
    const n = parseFloat(depositVal);
    if (!n || n <= 0) return;
    onAddDeposit(r._id, n);
    setDepositOpen(false);
    setDepositVal("");
  };

  return (
    <div className="ar-res-card">
      {/* Top row: avatar + info + status */}
      <div className="ar-res-card__top">
        <span className="ar-guest-ava-lg" style={{ background: avatarColor(index), flexShrink: 0 }}>
          {initials(r.guestName)}
        </span>
        <div className="ar-res-card__info">
          <div className="ar-res-card__name">{r.guestName}</div>
          {r.guestPhone && <div className="ar-res-card__contact">{r.guestPhone}</div>}
          {r.instagramName && (
            <div className="ar-res-card__contact" style={{ color: "#e1306c", fontWeight: 600 }}>
              @{r.instagramName}
            </div>
          )}
          <div className="ar-res-card__apt">{apt?.name || "—"}</div>
          <div className="ar-res-card__dates">
            {fmtDate(r.checkIn)} – {fmtDate(r.checkOut)}
            <span className="ar-res-card__nights"> · {nights} {nights === 1 ? "noć" : nights >= 5 ? "noći" : "noći"}</span>
          </div>
        </div>
        <Dropdown
          menu={{
            items: (Object.keys(STATUS_LABEL) as AptReservationStatus[]).map((s) => ({
              key: s,
              label: STATUS_LABEL[s],
              onClick: () => onStatusChange(r._id, s),
            })),
          }}
          trigger={["click"]}
        >
          <span className={`ar-status ar-status--${r.status}`} style={{ cursor: "pointer", flexShrink: 0, alignSelf: "flex-start" }}>
            {STATUS_LABEL[r.status as AptReservationStatus]} ▾
          </span>
        </Dropdown>
      </div>

      {/* Amount row */}
      <div className="ar-res-card__meta">
        <span className="ar-channel">
          <span className="ar-channel-dot" style={{ background: CHANNEL_DOT[r.channel] || "#6b6880" }} />
          {CHANNEL_LABEL[r.channel] || r.channel}
        </span>
        {r.amount > 0 && (
          <span className="ar-res-card__amount">{fmtAmount(r.amount, currency)}</span>
        )}
        {ps === "paid" && <span className="ar-pay-badge ar-pay-badge--paid">✓ Plaćeno</span>}
        {ps === "partial" && (
          <span className="ar-pay-badge ar-pay-badge--partial">◑ {fmtAmount(paid, currency)} / ostatak {fmtAmount(remaining, currency)}</span>
        )}
        {ps === "unpaid" && r.amount > 0 && <span className="ar-pay-badge ar-pay-badge--unpaid">○ Nije plaćeno</span>}
      </div>

      {r.packageName && (
        <div className="ar-pkg-badge" style={{ margin: "4px 0 0" }}>🎁 {r.packageName}</div>
      )}

      {/* Quick actions */}
      <div className="ar-res-card__actions">
        {nextStatus && (
          <button className="ar-res-card__action ar-res-card__action--status"
            onClick={() => onStatusChange(r._id, nextStatus.value)}>
            {nextStatus.label}
          </button>
        )}
        {ps !== "paid" && r.amount > 0 && (
          <button className="ar-res-card__action ar-res-card__action--pay"
            onClick={() => onMarkPaid(r._id)}>
            💳 Plaćeno sve
          </button>
        )}
        {ps !== "paid" && r.amount > 0 && (
          <button className="ar-res-card__action ar-res-card__action--deposit"
            onClick={() => { if (!depositOpen) setDepositVal(paid > 0 ? String(paid) : ""); setDepositOpen((v) => !v); }}>
            💰 Avans{paid > 0 ? ` (${fmtAmount(paid, currency)})` : ""}
          </button>
        )}
        <button className="ar-res-card__action ar-res-card__action--edit" onClick={onEdit}>
          ✏️ Uredi
        </button>
        <Popconfirm
          title="Izbriši rezervaciju?"
          onConfirm={onDelete}
          okText="Da"
          cancelText="Ne"
        >
          <button className="ar-res-card__action ar-res-card__action--del">🗑</button>
        </Popconfirm>
      </div>

      {/* Inline deposit input */}
      {depositOpen && (
        <div className="ar-res-card__deposit">
          <span className="ar-res-card__deposit-label">Iznos avansa ({currency})</span>
          <div className="ar-res-card__deposit-row">
            <input
              type="number"
              min={0}
              step={1}
              placeholder="0"
              value={depositVal}
              onChange={(e) => setDepositVal(e.target.value)}
              className="ar-res-card__deposit-input"
              autoFocus
            />
            <button className="ar-res-card__deposit-save" onClick={handleDeposit} disabled={!depositVal}>
              Snimi
            </button>
            <button className="ar-res-card__deposit-cancel" onClick={() => { setDepositOpen(false); setDepositVal(""); }}>
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const ApartmentReservations: React.FC = () => {
  const userData = useSelector((state: any) => state.userData.user);
  const rawClientId = userData?.clientId;
  const clientId: string =
    typeof rawClientId === "object" ? rawClientId?._id : rawClientId;

  // ─ Date navigation ─
  const [currentDate, setCurrentDate] = useState(dayjs());
  const year = currentDate.year();
  const month = currentDate.month() + 1;

  // ─ Data ─
  const [apartments, setApartments] = useState<ClientApartment[]>([]);
  const [reservations, setReservations] = useState<AptReservation[]>([]);
  const [events, setEvents] = useState<AptEvent[]>([]);
  const [stats, setStats] = useState<AptStats | null>(null);
  const [packages, setPackages] = useState<AptPackage[]>([]);
  const [aptIncomes, setAptIncomes] = useState<AptIncome[]>([]);
  const [aptExpenses, setAptExpenses] = useState<AptExpense[]>([]);
  const [financeLoading, setFinanceLoading] = useState(true);
  const [loading, setLoading] = useState(true);

  // ─ View ─
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("pregled");

  // ─ Modals / Drawers ─
  const [aptModalOpen, setAptModalOpen] = useState(false);
  const [editingApt, setEditingApt] = useState<ClientApartment | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingReservation, setEditingReservation] = useState<AptReservation | null>(null);
  const [defaultAptId, setDefaultAptId] = useState<string | undefined>();
  const [reservationPrefill, setReservationPrefill] = useState<{
    checkIn?: string;
    checkOut?: string;
    adults?: number;
    children?: number;
    extras?: ReservationExtra[];
  } | null>(null);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<AptEvent | null>(null);

  // ─ Load data ─
  const loadAll = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const [aptsRes, resRes, statsRes, evRes, pkgRes] = await Promise.all([
        getClientApartments(clientId),
        getAptReservations(clientId, {
          from: currentDate.startOf("month").toISOString(),
          to: currentDate.endOf("month").toISOString(),
          limit: 500,
        }),
        getAptStats(clientId, year, month),
        getAptEvents(clientId),
        getAptPackages(clientId),
      ]);
      setApartments(aptsRes.data);
      setReservations(resRes.data.reservations);
      setStats(statsRes.data);
      setEvents(evRes.data);
      setPackages(pkgRes.data);
    } catch {
      message.error("Greška pri učitavanju podataka");
    } finally {
      setLoading(false);
    }
  }, [clientId, year, month]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ─ Load additional income/expense (Finansije) — shared across Pregled KPI, AptCard and FinanceTab ─
  const loadFinance = useCallback(async () => {
    if (!clientId) return;
    setFinanceLoading(true);
    try {
      const monthStart = currentDate.startOf("month").toISOString();
      const monthEnd = currentDate.endOf("month").toISOString();
      const [incRes, expRes] = await Promise.all([
        getAptIncomes(clientId, { from: monthStart, to: monthEnd }),
        getAptExpenses(clientId, { from: monthStart, to: monthEnd }),
      ]);
      setAptIncomes(incRes.data);
      setAptExpenses(expRes.data);
    } catch {
      message.error("Greška pri učitavanju finansija");
    } finally {
      setFinanceLoading(false);
    }
  }, [clientId, year, month]);

  useEffect(() => {
    loadFinance();
  }, [loadFinance]);

  // ─ Handlers ─
  const handleDeleteReservation = async (id: string) => {
    try {
      await deleteAptReservation(id);
      message.success("Rezervacija izbrisana");
      loadAll();
    } catch {
      message.error("Greška pri brisanju");
    }
  };

  const handleDeleteApartment = async (id: string) => {
    try {
      await deleteClientApartment(id);
      message.success("Apartman izbrisan");
      loadAll();
    } catch {
      message.error("Greška pri brisanju apartmana");
    }
  };

  const handleDeleteEvent = async (id: string) => {
    try {
      await deleteAptEvent(id);
      message.success("Događaj izbrisan");
      loadAll();
    } catch {
      message.error("Greška pri brisanju događaja");
    }
  };

  const handleArchiveEvent = async (id: string, archived: boolean) => {
    try {
      await archiveAptEvent(id, archived);
      message.success(archived ? "Događaj arhiviran" : "Događaj vraćen iz arhive");
      loadAll();
    } catch {
      message.error("Greška pri arhiviranju");
    }
  };

  const handleStatusChange = async (id: string, status: AptReservationStatus) => {
    try {
      await updateAptReservationStatus(id, status);
      message.success("Status ažuriran");
      loadAll();
    } catch {
      message.error("Greška pri ažuriranju statusa");
    }
  };

  const handleMarkPaid = async (id: string) => {
    const res = reservations.find((x) => x._id === id);
    if (!res) return;
    try {
      await updateAptReservation(id, { paymentStatus: "paid", paidAmount: res.amount });
      message.success("Označeno kao plaćeno");
      loadAll();
    } catch {
      message.error("Greška pri ažuriranju");
    }
  };

  const handleAddDeposit = async (id: string, amount: number) => {
    try {
      await updateAptReservation(id, { paymentStatus: "partial", paidAmount: amount });
      message.success("Avans dodan");
      loadAll();
    } catch {
      message.error("Greška pri ažuriranju");
    }
  };

  const openNewEvent = () => {
    setEditingEvent(null);
    setEventModalOpen(true);
  };

  const openEditEvent = (ev: AptEvent) => {
    setEditingEvent(ev);
    setEventModalOpen(true);
  };

  const openNewReservation = (
    aptId?: string,
    prefill?: {
      checkIn?: string;
      checkOut?: string;
      adults?: number;
      children?: number;
      extras?: ReservationExtra[];
    } | null
  ) => {
    setEditingReservation(null);
    setDefaultAptId(aptId);
    setReservationPrefill(prefill ?? null);
    setDrawerOpen(true);
  };

  const openEditReservation = (r: AptReservation) => {
    setEditingReservation(r);
    setDrawerOpen(true);
  };

  const openNewApartment = () => {
    setEditingApt(null);
    setAptModalOpen(true);
  };

  const openEditApartment = (apt: ClientApartment) => {
    setEditingApt(apt);
    setAptModalOpen(true);
  };

  // ─ Filtered reservations ─
  const filteredReservations = useMemo(() => {
    // A reservation belongs to this month only if the guest spent at least 1 night here.
    // Nights span checkIn..checkOut-1, so we require: checkOut(date) > firstDayOfMonth.
    // Example: Jan 29 → Feb 1 checkout has 0 nights in February → excluded from Feb view.
    const mStart = currentDate.startOf("month").format("YYYY-MM-DD");
    const mEnd   = currentDate.endOf("month").format("YYYY-MM-DD");

    let list = reservations.filter((r) => {
      const ci = r.checkIn.substring(0, 10);
      const co = r.checkOut.substring(0, 10);
      return ci <= mEnd && co > mStart;
    });

    if (!searchQuery) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(
      (r) =>
        r.guestName.toLowerCase().includes(q) ||
        r.guestEmail?.toLowerCase().includes(q) ||
        r.guestPhone?.includes(q) ||
        r.instagramName?.toLowerCase().includes(q)
    );
  }, [reservations, searchQuery, currentDate]);

  // Ant Design Table treats a field named "children" as tree data.
  // Strip the "children" field (guest count) so Ant Design Table doesn't
  // confuse it with its own tree-data "children" prop.
  // Also merge events that overlap the current month into the same list.
  const tableRows = useMemo(() => {
    const mStart = currentDate.startOf("month").format("YYYY-MM-DD");
    const mEnd   = currentDate.endOf("month").format("YYYY-MM-DD");

    const resRows = filteredReservations.map(({ children: _kids, ...rest }) => ({
      ...rest,
      _rowKind: "res" as const,
    }));

    const q = searchQuery.toLowerCase();
    const eventRows = events
      .filter((ev) => {
        if (ev.archived) return false;
        const s = (ev.startDate || "").substring(0, 10);
        const e = (ev.endDate   || s).substring(0, 10);
        if (s > mEnd || e < mStart) return false;
        if (!q) return true;
        return (
          ev.title.toLowerCase().includes(q) ||
          (ev.contact || "").toLowerCase().includes(q)
        );
      })
      .map((ev) => ({
        _id:           ev._id,
        _rowKind:      "event" as const,
        _event:        ev,
        guestName:     ev.title,
        guestEmail:    ev.contact || undefined,
        guestPhone:    undefined,
        checkIn:       ev.startDate || "",
        checkOut:      ev.endDate   || ev.startDate || "",
        adults:        0,
        channel:       "direct" as any,
        status:        "event" as any,
        amount:        ev.cost || 0,
        isPaid:        false,
        paymentStatus: "unpaid" as any,
        paidAmount:    0,
        apartmentId:   ev.apartmentId || "",
        type:          "stay" as any,
        extras:        [] as any[],
        createdAt:     ev.createdAt,
        updatedAt:     ev.createdAt,
      }));

    return [...resRows, ...eventRows].sort((a, b) =>
      (a.checkIn || "").localeCompare(b.checkIn || "")
    );
  }, [filteredReservations, events, searchQuery, currentDate]);

  // ─ Export helpers ─
  const EXPORT_CHANNEL: Record<string, string> = {
    direct: "Direktno", booking: "Booking", airbnb: "Airbnb", phone: "Telefon", other: "Ostalo",
  };
  const EXPORT_TYPE: Record<string, string> = {
    standard: "Standardna", blogger: "Blogger", discount: "Akcija/Popust", private: "Privatno",
  };
  const EXPORT_PAYMENT: Record<string, string> = {
    paid: "Plaćeno", partial: "Djelimično", unpaid: "Nije plaćeno",
  };

  const buildExportRows = useCallback(() =>
    filteredReservations.map((r) => {
      const apt =
        typeof r.apartmentId === "object"
          ? (r.apartmentId as any)
          : apartments.find((a) => a._id === r.apartmentId);
      const aptName  = typeof r.apartmentId === "object" ? (r.apartmentId as any).name : apt?.name ?? "";
      const currency = apt?.currency ?? "RSD";
      const nights   = Math.max(0, dayjs(r.checkOut).diff(dayjs(r.checkIn), "day"));
      const paid     = r.paidAmount ?? 0;
      const remaining = Math.max(0, r.amount - paid);

      // ─ Akcija: paket s includes, promo, referral
      const pkg = r.packageId ? packages.find((p) => p._id === r.packageId) : null;
      const akcijaLines: string[] = [];
      if (r.packageName) {
        akcijaLines.push(r.packageName);
        const includeLabels = (pkg?.includes ?? []).map((inc) => inc.label);
        if (includeLabels.length > 0)
          akcijaLines.push(...includeLabels.map((l) => `- ${l}`));
      }
      if (r.promoCode) {
        const disc = r.promoDiscount ? ` (-${r.promoDiscount} ${currency})` : "";
        akcijaLines.push(`Promo: ${r.promoCode}${disc}`);
      }
      if (r.referralCode)
        akcijaLines.push(`Referral: ${r.referralCode}`);
      // ─ Dodaci (extras): svaki na posebnom redu
      const extrasLines = (r.extras ?? []).map(
        (e) => `  • ${e.name}: ${e.quantity} x ${e.pricePerUnit} ${currency} = ${e.total} ${currency}`
      );

      // ─ Obroci (mealSelections): grupisano po imenu obroka i stavke
      const mealsMap = new Map<string, { persons: number; price: number; included: boolean }>();
      for (const ms of (r.mealSelections ?? [])) {
        const key = `${ms.mealName} — ${ms.itemName}`;
        const prev = mealsMap.get(key) ?? { persons: 0, price: 0, included: ms.isIncluded };
        mealsMap.set(key, { persons: prev.persons + ms.persons, price: prev.price + ms.price, included: ms.isIncluded });
      }
      const mealsLines = Array.from(mealsMap.entries()).map(
        ([name, val]) =>
          val.included
            ? `  • ${name}: ${val.persons} os. (uključeno)`
            : `  • ${name}: ${val.persons} os. = ${val.price} ${currency}`
      );

      const hasExtras = extrasLines.length > 0;
      const hasMeals  = mealsLines.length > 0;

      const akcijaFull = [
        ...akcijaLines,
        ...(hasExtras ? ["Dodaci:"] : []),
        ...extrasLines,
        ...(hasMeals  ? ["Obroci:"] : []),
        ...mealsLines,
      ].join("\n");

      // Za Excel — ravne kolone za extras i meals
      const extrasFlat = (r.extras ?? []).map((e) => `${e.name} x${e.quantity} = ${e.total} ${currency}`).join("; ");
      const mealsFlat  = Array.from(mealsMap.entries())
        .map(([name, val]) => val.included ? `${name} (${val.persons} os., uključeno)` : `${name} (${val.persons} os., ${val.price} ${currency})`)
        .join("; ");

      return {
        "Ime gosta":            r.guestName,
        "Email":                r.guestEmail ?? "",
        "Telefon":              r.guestPhone ?? "",
        "Instagram":            r.instagramName ?? "",
        "Apartman":             aptName,
        "Check-in":             dayjs(r.checkIn).format("DD.MM.YYYY"),
        "Check-out":            dayjs(r.checkOut).format("DD.MM.YYYY"),
        "Broj noći":            nights,
        "Odrasli":              r.adults,
        "Djeca":                r.children ?? 0,
        "Kanal":                EXPORT_CHANNEL[r.channel] ?? r.channel,
        "Status":               STATUS_LABEL[r.status] ?? r.status,
        "Tip":                  EXPORT_TYPE[r.type] ?? r.type,
        "Akcija":               akcijaLines.join("; "),
        "Dodaci":               extrasFlat,
        "Obroci":               mealsFlat,
        "Osnova cijene":        r.baseAmount ?? r.amount,
        "Popust":               r.promoDiscount ?? 0,
        "Iznos ukupno":         r.amount,
        "Valuta":               currency,
        "Plaćeno":              paid,
        "Preostalo za platiti": remaining,
        "Status plaćanja":      EXPORT_PAYMENT[r.paymentStatus] ?? r.paymentStatus,
        "Napomena":             r.note ?? "",
        _akcijaFull:            akcijaFull,
      };
    })
  , [filteredReservations, apartments, packages]);

  const exportToExcel = useCallback(() => {
    const rawRows = buildExportRows();
    if (!rawRows.length) { message.warning("Nema podataka za export"); return; }
    // Strip internal field before writing
    const rows = rawRows.map(({ _akcijaFull: _, ...rest }) => rest);
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = Object.keys(rows[0]).map((key) => ({
      wch: Math.max(key.length, ...rows.map((r) => String((r as any)[key] ?? "").length)) + 2,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Rezervacije");
    XLSX.writeFile(wb, `rezervacije-${currentDate.format("YYYY-MM")}.xlsx`);
  }, [buildExportRows, currentDate]);

  const exportToPdf = useCallback(() => {
    const rows = buildExportRows();
    if (!rows.length) { message.warning("Nema podataka za export"); return; }

    const DIACR: Record<string, string> = {
      "ć":"c", "č":"c", "š":"s", "ž":"z", "đ":"dj",
      "Ć":"C", "Č":"C", "Š":"S", "Ž":"Z", "Đ":"Dj",
      "—":"-", "–":"-", "•":"*",
    };
    const tr = (s: string | number) =>
      String(s ?? "").replace(/[ćčšžđĆČŠŽĐ—–•]/g, (c) => DIACR[c] ?? c);

    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(14);
    doc.text(tr(`Rezervacije apartmana — ${currentDate.format("MMMM YYYY")}`), 14, 14);
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(tr(`Ukupno: ${rows.length} rezervacija`), 14, 20);
    doc.setTextColor(0);

    autoTable(doc, {
      startY: 25,
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [91, 80, 214] },
      alternateRowStyles: { fillColor: [245, 244, 253] },
      head: [[
        "Gost", "Apartman", "Check-in", "Check-out", "Noci",
        "Kanal", "Status", "Akcija (detalji)",
        "Iznos", "Val.", "Placeno", "Preostalo", "Napomena",
      ]],
      body: rows.map((r) => [
        tr(r["Ime gosta"]) + (r["Telefon"] ? `\n${r["Telefon"]}` : ""),
        tr(r["Apartman"]),
        r["Check-in"],
        r["Check-out"],
        r["Broj noći"],
        tr(r["Kanal"]),
        tr(r["Status"]),
        tr(r["_akcijaFull"] || r["Tip"]),
        r["Iznos ukupno"],
        r["Valuta"],
        r["Plaćeno"],
        r["Preostalo za platiti"] > 0 ? r["Preostalo za platiti"] : "",
        tr(r["Napomena"]),
      ]),
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 22 },
        7: { cellWidth: 52 },
        12: { cellWidth: 24 },
      },
    });

    doc.save(`rezervacije-${currentDate.format("YYYY-MM")}.pdf`);
  }, [buildExportRows, currentDate]);

  const exportEventsToExcel = useCallback(() => {
    const TYPE_LABEL: Record<string, string> = { blogger: "Blogger", discount: "Popust" };
    const rows = events.map((ev) => {
      const aptName =
        typeof ev.apartmentId === "object"
          ? (ev.apartmentId as any).name
          : ev.apartmentName ?? ev.apartmentId ?? "";
      return {
        "Naziv":         ev.title,
        "Podnaslov":     ev.sub ?? "",
        "Tip":           TYPE_LABEL[ev.type] ?? ev.type,
        "Apartman":      aptName,
        "Početak":       ev.startDate ? dayjs(ev.startDate).format("DD.MM.YYYY") : "",
        "Kraj":          ev.endDate   ? dayjs(ev.endDate).format("DD.MM.YYYY")   : "",
        "Noći":          ev.nights,
        "Trošak":        ev.cost,
        "Extra trošak":  ev.extraCost,
        "Kontakt":       ev.contact ?? "",
        "Deliverables":  ev.deliverables ?? "",
        "Arhiviran":     ev.archived ? "Da" : "Ne",
      };
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const colWidths = Object.keys(rows[0] ?? {}).map((key) => ({
      wch: Math.max(key.length, ...rows.map((r) => String((r as any)[key] ?? "").length)) + 2,
    }));
    ws["!cols"] = colWidths;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Događaji");
    XLSX.writeFile(wb, `dogadjaji-${dayjs().format("YYYY-MM")}.xlsx`);
  }, [events]);

  // ─ Table columns ─
  const columns: ColumnsType<any> = [
    {
      title: "Gost",
      key: "guest",
      render: (_, r, i) => {
        if (r._rowKind === "event") {
          return (
            <div className="ar-guest-cell">
              <span className="ar-guest-ava-lg" style={{ background: "#7c3aed", fontSize: 16 }}>
                📅
              </span>
              <div>
                <div className="ar-guest-name">{r.guestName}</div>
                <div className="ar-guest-meta" style={{ color: "#7c3aed" }}>
                  {r.guestEmail || "Događaj"}
                </div>
              </div>
            </div>
          );
        }
        return (
          <div className="ar-guest-cell">
            <span className="ar-guest-ava-lg" style={{ background: avatarColor(i) }}>
              {initials(r.guestName)}
            </span>
            <div>
              <div className="ar-guest-name">{r.guestName}</div>
              <div className="ar-guest-meta">{r.guestEmail || r.guestPhone || "—"}</div>
              {r.packageName && (
                <div className="ar-pkg-badge">
                  🎁 {r.packageName}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Apartman",
      key: "apt",
      render: (_, r) => {
        const name =
          typeof r.apartmentId === "object"
            ? (r.apartmentId as any).name
            : apartments.find((a) => a._id === r.apartmentId)?.name || "—";
        return (
          <span style={{ fontWeight: 550, color: "#3d3650" }}>{name}</span>
        );
      },
    },
    {
      title: "Boravak",
      key: "stay",
      render: (_, r) => {
        const nights = dayjs(r.checkOut).diff(dayjs(r.checkIn), "day");
        return (
          <div>
            <div className="ar-stay-dates">
              {fmtDate(r.checkIn)} – {fmtDate(r.checkOut)}
            </div>
            <div className="ar-stay-nights">
              {nights} {nights === 1 ? "noć" : "noći"}
            </div>
          </div>
        );
      },
    },
    {
      title: "Kanal",
      key: "channel",
      render: (_, r) => {
        if (r._rowKind === "event") {
          return (
            <span className="ar-channel">
              <span className="ar-channel-dot" style={{ background: "#7c3aed" }} />
              Događaj
            </span>
          );
        }
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <span className="ar-channel">
              <span
                className="ar-channel-dot"
                style={{ background: CHANNEL_DOT[r.channel] || "#6b6880" }}
              />
              {CHANNEL_LABEL[r.channel] || r.channel}
            </span>
            {r.referralCode && (
              <span className="ar-ref-source">🔗 {r.referralCode}</span>
            )}
            {r.promoCode && (
              <span className="ar-ref-source" style={{ color: "#15803d", background: "#dcfce7" }}>
                🎉 {r.promoCode}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: "Status",
      key: "status",
      render: (_, r) => {
        if (r._rowKind === "event") {
          return (
            <span className="ar-status" style={{ background: "rgba(124,58,237,0.1)", color: "#7c3aed", border: "1px solid rgba(124,58,237,0.2)" }}>
              📅 Događaj
            </span>
          );
        }
        return (
          <Dropdown
            menu={{
              items: (Object.keys(STATUS_LABEL) as AptReservationStatus[]).map((s) => ({
                key: s,
                label: STATUS_LABEL[s],
                onClick: () => handleStatusChange(r._id, s),
              })),
            }}
            trigger={["click"]}
          >
            <span className={`ar-status ar-status--${r.status}`} style={{ cursor: "pointer" }}>
              {STATUS_LABEL[r.status as AptReservationStatus]}
            </span>
          </Dropdown>
        );
      },
    },
    {
      title: "Iznos",
      key: "amount",
      align: "right" as const,
      render: (_, r) => {
        const aptId = typeof r.apartmentId === "object" ? (r.apartmentId as any)._id : r.apartmentId;
        const currency = apartments.find((a) => a._id === aptId)?.currency;
        const ps = r.paymentStatus || (r.isPaid ? "paid" : "unpaid");
        const paid = r.paidAmount ?? (r.isPaid ? r.amount : 0);
        const remaining = Math.max(0, (r.amount || 0) - paid);
        const nights = r._rowKind !== "event" ? Math.max(1, dayjs(r.checkOut).diff(dayjs(r.checkIn), "day")) : 0;
        const perNight = r.amount > 0 && nights > 0 ? Math.round(r.amount / nights) : null;
        return (
          <div className={`ar-amount ar-amount--${ps}`}>
            {r.amount > 0 ? (
              <>
                <div className="ar-amount-total">{fmtAmount(r.amount, currency)}</div>
                {perNight != null && (
                  <div style={{ fontSize: 11, color: "#9893a8", marginTop: 1 }}>
                    {fmtAmount(perNight, currency)} / noć
                  </div>
                )}
                {ps === "paid" && <span className="ar-pay-badge ar-pay-badge--paid">✓ Plaćeno</span>}
                {ps === "partial" && (
                  <div className="ar-amount-breakdown">
                    <span className="ar-pay-badge ar-pay-badge--partial">◑ Avans: {fmtAmount(paid, currency)}</span>
                    <span className="ar-pay-badge ar-pay-badge--remaining">Ostatak: {fmtAmount(remaining, currency)}</span>
                  </div>
                )}
                {ps === "unpaid" && r.amount > 0 && <span className="ar-pay-badge ar-pay-badge--unpaid">○ Nije plaćeno</span>}
              </>
            ) : (
              <span style={{ color: "#9893a8" }}>Free</span>
            )}
          </div>
        );
      },
    },
    {
      title: "",
      key: "actions",
      render: (_, row) => {
        if (row._rowKind === "event") {
          return (
            <div className="ar-row-actions">
              <Tooltip title="Uredi događaj">
                <button onClick={() => openEditEvent(row._event)}>
                  <EditOutlined style={{ fontSize: 12 }} />
                </button>
              </Tooltip>
            </div>
          );
        }
        const r = filteredReservations.find((x) => x._id === row._id) ?? (row as any);
        return (
          <div className="ar-row-actions">
            <Tooltip title="Izmijeni">
              <button onClick={() => openEditReservation(r)}>
                <EditOutlined style={{ fontSize: 12 }} />
              </button>
            </Tooltip>
            <Popconfirm
              title="Izbriši rezervaciju?"
              onConfirm={() => handleDeleteReservation(r._id)}
              okText="Da"
              cancelText="Ne"
            >
              <Tooltip title="Izbriši">
                <button>
                  <DeleteOutlined style={{ fontSize: 12 }} />
                </button>
              </Tooltip>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  // ─ KPI ─
  const kpi = stats ?? {
    occupancyRate: 0,
    totalReservations: 0,
    revenue: 0,
    paidRevenue: 0,
    pendingRevenue: 0,
    freeNights: 0,
    totalNights: 0,
    occupiedNights: 0,
    apartments: 0,
  };

  // Revenue grouped by currency — prorated: only days in current month count
  const revenuePerCurrency = useMemo(() => {
    const mStart      = dayjs(new Date(year, month - 1, 1));
    const mNextStart  = dayjs(new Date(year, month, 1));

    const map: Record<string, { total: number; paid: number; pending: number }> = {};
    reservations.forEach((r) => {
      if (r.status === "canceled" || r.status === "noshow") return;
      const aptId    = typeof r.apartmentId === "object" ? (r.apartmentId as any)._id : r.apartmentId;
      const currency = apartments.find((a) => a._id === aptId)?.currency || "RSD";
      if (!map[currency]) map[currency] = { total: 0, paid: 0, pending: 0 };

      const ci       = dayjs(r.checkIn.substring(0, 10));
      const co       = dayjs(r.checkOut.substring(0, 10));
      const resNights = Math.max(1, co.diff(ci, "day"));
      const start    = ci.isBefore(mStart) ? mStart : ci;
      const end      = co.isAfter(mNextStart) ? mNextStart : co;
      const nightsInMonth = Math.max(0, end.diff(start, "day"));
      const ratio    = nightsInMonth / resNights;

      const fullAmount = r.amount || 0;
      const fullPaid   = r.paidAmount ?? (r.isPaid ? fullAmount : 0);
      map[currency].total   += fullAmount * ratio;
      map[currency].paid    += fullPaid   * ratio;
      map[currency].pending += Math.max(0, fullAmount - fullPaid) * ratio;
    });
    return map;
  }, [reservations, apartments, year, month]);

  // Average revenue per night — per currency, only for confirmed/pending in the current month
  const avgPerNight = useMemo(() => {
    const mStart     = dayjs(new Date(year, month - 1, 1));
    const mNextStart = dayjs(new Date(year, month, 1));
    const map: Record<string, { revenue: number; nights: number }> = {};
    reservations.forEach((r) => {
      if (r.status === "canceled" || r.status === "noshow") return;
      const aptId    = typeof r.apartmentId === "object" ? (r.apartmentId as any)._id : r.apartmentId;
      const currency = apartments.find((a) => a._id === aptId)?.currency || "RSD";
      if (!map[currency]) map[currency] = { revenue: 0, nights: 0 };
      const ci       = dayjs(r.checkIn.substring(0, 10));
      const co       = dayjs(r.checkOut.substring(0, 10));
      const resNights = Math.max(1, co.diff(ci, "day"));
      const start    = ci.isBefore(mStart) ? mStart : ci;
      const end      = co.isAfter(mNextStart) ? mNextStart : co;
      const nightsInMonth = Math.max(0, end.diff(start, "day"));
      if (nightsInMonth === 0) return;
      const ratio    = nightsInMonth / resNights;
      map[currency].revenue += (r.amount || 0) * ratio;
      map[currency].nights  += nightsInMonth;
    });
    const result: Record<string, number> = {};
    Object.entries(map).forEach(([currency, { revenue, nights }]) => {
      if (nights > 0) result[currency] = Math.round(revenue / nights);
    });
    return result;
  }, [reservations, apartments, year, month]);

  // Real earnings per currency: reservation revenue + additional income − expenses
  const realEarningsPerCurrency = useMemo(() => {
    const map: Record<string, { reservation: number; income: number; expense: number; net: number }> = {};
    const ensure = (currency: string) => {
      if (!map[currency]) map[currency] = { reservation: 0, income: 0, expense: 0, net: 0 };
      return map[currency];
    };
    Object.entries(revenuePerCurrency).forEach(([currency, rev]) => {
      ensure(currency).reservation += rev.total;
    });
    aptIncomes.forEach((i) => { ensure(i.currency).income += i.amount || 0; });
    aptExpenses.forEach((e) => { ensure(e.currency).expense += e.amount || 0; });
    Object.values(map).forEach((v) => { v.net = v.reservation + v.income - v.expense; });
    return map;
  }, [revenuePerCurrency, aptIncomes, aptExpenses]);

  const prevMonth = currentDate.subtract(1, "month");
  const nextMonth = currentDate.add(1, "month");

  const monthLabel = `${MONTH_NAMES[month - 1]} ${year}`;
  const monthRange = `01.${String(month).padStart(2, "0")} – ${new Date(year, month, 0).getDate()}.${String(month).padStart(2, "0")}`;

  // ─ Apartmani tab ─
  const ApartmaniTab = (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: "#6b6880" }}>
            {apartments.length} {apartments.length === 1 ? "apartman" : "apartmana"} u sistemu
          </span>
          {/* Public listing URL */}
          {clientId && (
            <Tooltip title="Kopiraj javni URL za sve apartmane">
              <button
                className="ar-btn ar-btn--ghost"
                style={{ fontSize: 12 }}
                onClick={() => {
                  const url = `${window.location.origin}/apartments/public?clientId=${clientId}`;
                  navigator.clipboard.writeText(url);
                  message.success("URL kopiran u clipboard! Podijeli ga sa gostima.");
                }}
              >
                <GlobalOutlined /> Kopiraj javni URL (sve)
              </button>
            </Tooltip>
          )}
        </div>
        <button className="ar-btn ar-btn--primary" onClick={openNewApartment}>
          <PlusOutlined />
          Dodaj apartman
        </button>
      </div>

      {apartments.length === 0 ? (
        <div className="ar-empty">
          <HomeOutlined style={{ fontSize: 42, opacity: 0.4 }} />
          <div className="t">Nema apartmana</div>
          <div className="s">Dodajte vaše apartmane kako biste mogli upravljati rezervacijama</div>
          <button
            className="ar-btn ar-btn--primary"
            style={{ marginTop: 16 }}
            onClick={openNewApartment}
          >
            <PlusOutlined />
            Dodaj prvi apartman
          </button>
        </div>
      ) : (
        <div className="ar-apt-list">
          {apartments.map((apt, i) => (
            <div key={apt._id} className="ar-apt-list-item">
              <div
                className="ar-apt-list-cover"
                style={{
                  background:
                    apt.coverGradient ||
                    `linear-gradient(135deg, ${avatarColor(i)}, ${avatarColor(i + 3)})`,
                }}
              />
              <div className="ar-apt-list-info">
                <div className="name">{apt.name}</div>
                <div className="meta">
                  <span>📍 {apt.location}</span>
                  <span>·</span>
                  <span>👤 {apt.capacity} gosta</span>
                  <span>·</span>
                  <span style={{ textTransform: "capitalize" }}>{apt.type}</span>
                  {apt.tags?.slice(0, 2).map((t) => (
                    <span key={t} className="ar-pill">{t}</span>
                  ))}
                  {!apt.isActive && (
                    <Tag color="default">Neaktivan</Tag>
                  )}
                </div>
              </div>
              <div className="ar-apt-list-price">
                {fmtAmount(apt.pricePerNight, apt.currency)}
                <span className="per">/noć</span>
                {(apt.minNights ?? 1) > 1 && (
                  <span style={{ fontSize: 11, color: "#ca8a04", marginLeft: 6, fontWeight: 600 }}>
                    min. {apt.minNights} noći
                  </span>
                )}
                {(apt.pricingRules?.length ?? 0) > 0 && (
                  <span style={{ fontSize: 11, color: "#5b50d6", marginLeft: 6, fontWeight: 600 }}>
                    {apt.pricingRules.length} posebn{apt.pricingRules.length === 1 ? "a" : "ih"} cijene
                  </span>
                )}
              </div>
              <div className="ar-apt-list-actions">
                <Tooltip title="Kopiraj javni URL apartmana">
                  <button
                    className="ar-btn ar-btn--ghost"
                    style={{ width: 32, padding: 0, justifyContent: "center", color: "#5b50d6" }}
                    onClick={() => {
                      const url = `${window.location.origin}/apartments/public/${apt._id}?clientId=${clientId}`;
                      navigator.clipboard.writeText(url);
                      message.success(`URL za "${apt.name}" kopiran!`);
                    }}
                  >
                    <LinkOutlined style={{ fontSize: 13 }} />
                  </button>
                </Tooltip>
                <Tooltip title="Izmijeni">
                  <button
                    className="ar-btn ar-btn--ghost"
                    style={{ width: 32, padding: 0, justifyContent: "center" }}
                    onClick={() => openEditApartment(apt)}
                  >
                    <EditOutlined style={{ fontSize: 13 }} />
                  </button>
                </Tooltip>
                <Popconfirm
                  title="Izbriši apartman? Ovo će ukloniti sve asocirane rezervacije."
                  onConfirm={() => handleDeleteApartment(apt._id)}
                  okText="Da, izbriši"
                  cancelText="Otkaži"
                  okButtonProps={{ danger: true }}
                >
                  <Tooltip title="Izbriši">
                    <button
                      className="ar-btn ar-btn--ghost"
                      style={{
                        width: 32,
                        padding: 0,
                        justifyContent: "center",
                        color: "#dc2626",
                      }}
                    >
                      <DeleteOutlined style={{ fontSize: 13 }} />
                    </button>
                  </Tooltip>
                </Popconfirm>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // ─ Stable sub-tab instances (must not remount on parent re-render) ─
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const statTabContent = React.useMemo(() => <StatsTab clientId={clientId} />, [clientId]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const referralsTabContent = React.useMemo(() => <ReferralsTab clientId={clientId} />, [clientId]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const promoCodesTabContent = React.useMemo(() => <PromoCodesTab clientId={clientId} />, [clientId]);

  const financeTabContent = React.useMemo(
    () => (
      <FinanceTab
        clientId={clientId}
        apartments={apartments}
        reservationRevenue={revenuePerCurrency}
        incomes={aptIncomes}
        expenses={aptExpenses}
        loading={financeLoading}
        onChanged={loadFinance}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientId, apartments, revenuePerCurrency, aptIncomes, aptExpenses, financeLoading, loadFinance]
  );

  // ─ Tab items ─
  const tabItems = [
    {
      key: "pregled",
      label: (
        <span>
          Pregled
        </span>
      ),
      children: (
        <div>
          {/* Toolbar */}
          <div className="ar-toolbar">
            <div className="ar-view">
              <button
                className={viewMode === "grid" ? "active" : ""}
                onClick={() => setViewMode("grid")}
              >
                <AppstoreOutlined /> Grid
              </button>
              <button
                className={viewMode === "list" ? "active" : ""}
                onClick={() => setViewMode("list")}
              >
                <UnorderedListOutlined /> Lista
              </button>
            </div>

            <div className="ar-search">
              🔍
              <input
                type="text"
                placeholder="Pretraži po gostu..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <button className="ar-btn ar-btn--ghost" style={{ marginLeft: "auto" }}>
              Filteri
            </button>
            <button className="ar-btn ar-btn--ghost" onClick={exportToPdf}>
              <FilePdfOutlined /> PDF
            </button>
            <button className="ar-btn ar-btn--ghost" onClick={exportToExcel}>
              <FileExcelOutlined /> Excel
            </button>
          </div>

          {viewMode === "grid" ? (
            <div className="ar-apt-grid">
              {apartments.map((apt, i) => (
                <AptCard
                  key={apt._id}
                  apt={apt}
                  reservations={reservations}
                  events={events}
                  aptIncomes={aptIncomes}
                  aptExpenses={aptExpenses}
                  packages={packages}
                  year={year}
                  month={month}
                  onEdit={openEditApartment}
                  onDelete={handleDeleteApartment}
                  onNewReservation={openNewReservation}
                  onEditReservation={openEditReservation}
                  index={i}
                />
              ))}
              {apartments.length === 0 && !loading && (
                <div
                  className="ar-empty"
                  style={{ gridColumn: "1/-1" }}
                >
                  <HomeOutlined style={{ fontSize: 42, opacity: 0.4 }} />
                  <div className="t">Nema apartmana</div>
                  <div className="s">
                    Dodajte apartmane u tabu "Apartmani" da biste počeli upravljati
                    rezervacijama
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="ar-table-wrap">
              <Table
                columns={columns}
                dataSource={tableRows}
                rowKey="_id"
                childrenColumnName="__subRows__"
                scroll={{ x: 800 }}
                pagination={{ pageSize: 20, showTotal: (t) => `Ukupno ${t}` }}
                locale={{ emptyText: "Nema rezervacija za ovaj period" }}
              />
            </div>
          )}
        </div>
      ),
    },
    {
      key: "lista",
      label: (
        <span>
          Sve rezervacije{" "}
          <span className="ar-tab-count">{tableRows.length}</span>
        </span>
      ),
      children: (
        <div>
          <div className="ar-toolbar">
            <div className="ar-search">
              🔍
              <input
                type="text"
                placeholder="Pretraži po gostu, emailu..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button className="ar-btn ar-btn--ghost" style={{ marginLeft: "auto" }}>
              Filteri
            </button>
            <button className="ar-btn ar-btn--ghost" onClick={exportToPdf}>
              <FilePdfOutlined /> PDF
            </button>
            <button className="ar-btn ar-btn--ghost" onClick={exportToExcel}>
              <FileExcelOutlined /> Excel
            </button>
          </div>

          {/* Mobile cards */}
          <div className="ar-res-cards">
            {tableRows.filter((r) => r._rowKind === "res").map((row, i) => {
              const r = filteredReservations.find((x) => x._id === row._id) ?? (row as any);
              const aptId = typeof r.apartmentId === "object" ? (r.apartmentId as any)._id : r.apartmentId;
              const apt = apartments.find((a) => a._id === aptId);
              return (
                <ResMobileCard
                  key={r._id}
                  r={r}
                  apt={apt}
                  index={i}
                  onEdit={() => openEditReservation(r)}
                  onDelete={() => handleDeleteReservation(r._id)}
                  onStatusChange={handleStatusChange}
                  onMarkPaid={handleMarkPaid}
                  onAddDeposit={handleAddDeposit}
                />
              );
            })}
            {tableRows.filter((r) => r._rowKind === "res").length === 0 && (
              <div style={{ textAlign: "center", padding: "40px 0", color: "#9893a8", fontSize: 14 }}>
                Nema rezervacija za ovaj period
              </div>
            )}
          </div>

          {/* Desktop table */}
          <div className="ar-table-wrap ar-table-desktop-only">
            <Table
              columns={columns}
              dataSource={tableRows}
              rowKey="_id"
              childrenColumnName="__subRows__"
              pagination={{ pageSize: 20, showTotal: (t) => `Ukupno ${t}` }}
              locale={{ emptyText: "Nema rezervacija za ovaj period" }}
            />
          </div>
        </div>
      ),
    },
    {
      key: "dogadjaji",
      label: (
        <span>
          Događaji{" "}
          <span className="ar-tab-count">{events.length}</span>
        </span>
      ),
      children: (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 16 }}>
            <button className="ar-btn ar-btn--ghost" onClick={exportEventsToExcel}>Izvoz Excel</button>
            <button className="ar-btn ar-btn--primary" onClick={openNewEvent}>
              <PlusOutlined />
              Dodaj događaj
            </button>
          </div>
          <EventsGrid
            events={events}
            onEdit={openEditEvent}
            onDelete={handleDeleteEvent}
            onArchive={handleArchiveEvent}
          />
        </div>
      ),
    },
    {
      key: "apartmani",
      label: (
        <span>
          Apartmani{" "}
          <span className="ar-tab-count">{apartments.length}</span>
        </span>
      ),
      children: ApartmaniTab,
    },
    ...(isAdmin(userData?.roles)
      ? [
          {
            key: "calculator",
            label: <span>Kalkulator</span>,
            children: (
              <CalculatorTab
                clientId={clientId}
                apartments={apartments}
                onApartmentsChange={(updated: ClientApartment) =>
                  setApartments((prev) => prev.map((a) => (a._id === updated._id ? updated : a)))
                }
                onCreateReservation={(aptId, prefill) => openNewReservation(aptId, prefill)}
              />
            ),
          },
        ]
      : []),
    {
      key: "gosti",
      label: <span>Gosti</span>,
      children: <GuestsTab clientId={clientId} />,
    },
    {
      key: "finansije",
      label: "Finansije",
      children: financeTabContent,
    },
    {
      key: "stat",
      label: "Statistika",
      children: statTabContent,
    },
    {
      key: "referrali",
      label: "Referrali",
      children: referralsTabContent,
    },
    {
      key: "promo",
      label: "Promo kodovi",
      children: promoCodesTabContent,
    },
    {
      key: "akcije",
      label: "Akcije",
      children: (
        <PackagesTab clientId={clientId} apartments={apartments} />
      ),
    },
    {
      key: "obroci",
      label: "Obroci",
      children: (
        <MealsTab
          clientId={clientId}
          apartments={apartments}
          onApartmentsChange={(updated) =>
            setApartments((prev) => prev.map((a) => a._id === updated._id ? updated : a))
          }
        />
      ),
    },
    {
      key: "meni",
      label: "Meni",
      children: (
        <MenuTab
          clientId={clientId}
          apartments={apartments}
          onApartmentsChange={(updated) =>
            setApartments((prev) => prev.map((a) => a._id === updated._id ? updated : a))
          }
        />
      ),
    },
    {
      key: "meni-narudzbe",
      label: "Narudžbe",
      children: (
        <MenuOrdersTab clientId={clientId} apartments={apartments} />
      ),
    },
    {
      key: "blokade",
      label: "Blokade",
      children: (
        <BlockedDatesTab
          apartments={apartments}
          onApartmentsChange={(updated) =>
            setApartments((prev) => prev.map((a) => a._id === updated._id ? updated : a))
          }
        />
      ),
    },
  ];

  // ─ Mobile tab config (synced with desktop Ant tabs) ─
  const MOBILE_TABS = [
    { key: "pregled",    label: "Pregled" },
    { key: "lista",      label: "Rezervacije", count: reservations.length },
    { key: "dogadjaji",  label: "Događaji",    count: events.length },
    { key: "apartmani",  label: "Apartmani",   count: apartments.length },
    ...(isAdmin(userData?.roles) ? [{ key: "calculator", label: "Kalkulator" }] : []),
    { key: "gosti",      label: "Gosti" },
    { key: "finansije",  label: "Finansije" },
    { key: "stat",       label: "Statistika" },
    { key: "referrali",  label: "Referrali" },
    { key: "promo",      label: "Promo kodovi" },
    { key: "akcije",     label: "Akcije" },
    { key: "obroci",     label: "Obroci" },
    { key: "meni",            label: "Meni" },
    { key: "meni-narudzbe",   label: "Narudžbe" },
    { key: "blokade",         label: "Blokade" },
  ];

  // ─ Render ─
  return (
    <div className="apt-res-page">

      {/* ── Mobile sticky topbar (hidden on desktop) ── */}
      <div className="ar-m-topbar">
        <button className="ar-m-tb-btn" onClick={() => setCurrentDate(prevMonth)}>‹</button>
        <div className="ar-m-tb-title">
          <span className="t">{monthLabel}</span>
          <span className="s">{reservations.length} rezervacija</span>
        </div>
        <button className="ar-m-tb-btn" onClick={() => setCurrentDate(nextMonth)}>›</button>
        <button
          className="ar-m-tb-btn"
          onClick={loadAll}
          disabled={loading}
          title="Osvježi"
        >
          <span className={loading ? "ar-refresh-spin" : ""}>↻</span>
        </button>
      </div>

      {/* ── Mobile sticky tab chips (hidden on desktop) ── */}
      <nav className="ar-m-tabbar">
        {MOBILE_TABS.map((tab) => (
          <button
            key={tab.key}
            className={`ar-m-tab-chip${activeTab === tab.key ? " active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="cnt">{tab.count}</span>
            )}
          </button>
        ))}
      </nav>

      {/* Header */}
      <div className="ar-head">
        <div className="ar-head__left">
          <h1 className="ar-head__title">Rezervacije apartmana</h1>
          <p className="ar-head__sub">
            Pregled rezervacija, gostiju i događaja kroz sve apartmane ·{" "}
            {monthLabel}
          </p>
        </div>
        <div className="ar-head__actions">
          <button
            className="ar-btn ar-btn--ghost ar-refresh-btn"
            onClick={loadAll}
            disabled={loading}
          >
            <span className={loading ? "ar-refresh-spin" : ""}>↻</span>
            <span className="ar-refresh-label">Osvježi</span>
          </button>
          <button
            className="ar-btn ar-btn--primary"
            onClick={() => openNewReservation()}
          >
            <PlusOutlined />
            Nova rezervacija
          </button>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="ar-kpi">
        <div className="ar-kpi-card ar-kpi-card--featured">
          <div className="ar-kpi-label">
            <span className="ic">⏱</span>
            Popunjenost
          </div>
          <div className="ar-kpi-value">
            {kpi.occupancyRate}
            <span className="unit">%</span>
          </div>
          <div className="ar-bar-track">
            <div
              className="ar-bar-fill"
              style={{ width: `${kpi.occupancyRate}%` }}
            />
          </div>
          <div className="ar-kpi-foot">
            <span>
              {kpi.occupiedNights} / {kpi.totalNights} noći ({kpi.apartments} apt.)
            </span>
          </div>
        </div>

        <div className="ar-kpi-card">
          <div className="ar-kpi-label">
            <span className="ic">📋</span>
            Rezervacije
          </div>
          <div className="ar-kpi-value">{kpi.totalReservations}</div>
          <div className="ar-kpi-foot">
            <span>ovaj mjesec</span>
          </div>
        </div>

        <div className="ar-kpi-card">
          <div className="ar-kpi-label">
            <span className="ic">💳</span>
            Prihod ovaj mjesec
          </div>
          {Object.keys(revenuePerCurrency).length === 0 ? (
            <div className="ar-kpi-value">0</div>
          ) : (
            Object.entries(revenuePerCurrency).map(([currency, rev]) => (
              <div key={currency} className="ar-kpi-value" style={{ fontSize: rev.total >= 10000 ? 18 : undefined }}>
                {fmtAmount(rev.total, currency)}
              </div>
            ))
          )}
          <div className="ar-kpi-foot">
            {Object.keys(revenuePerCurrency).length === 0 ? (
              <span>0 naplaćeno · 0 pending</span>
            ) : (
              Object.entries(revenuePerCurrency).map(([currency, rev]) => (
                <span key={currency} style={{ display: "block" }}>
                  {fmtAmount(rev.paid, currency)} naplaćeno · {fmtAmount(rev.pending, currency)} pending
                </span>
              ))
            )}
          </div>
        </div>

        <div className="ar-kpi-card">
          <div className="ar-kpi-label">
            <span className="ic">📈</span>
            Realna zarada
          </div>
          {Object.keys(realEarningsPerCurrency).length === 0 ? (
            <div className="ar-kpi-value">0</div>
          ) : (
            Object.entries(realEarningsPerCurrency).map(([currency, e]) => (
              <div key={currency} className="ar-kpi-value" style={{ fontSize: e.net >= 10000 ? 18 : undefined }}>
                {e.net >= 0 ? "+" : ""}{fmtAmount(e.net, currency)}
              </div>
            ))
          )}
          <div className="ar-kpi-foot">
            <span>prihodi + dodatni prihod − rashodi</span>
          </div>
        </div>

        <div className="ar-kpi-card">
          <div className="ar-kpi-label">
            <span className="ic">🏠</span>
            Slobodne noći
          </div>
          <div className="ar-kpi-value">
            {kpi.freeNights}
            <span className="unit">/ {kpi.totalNights}</span>
          </div>
          <div className="ar-kpi-foot">
            <span>{apartments.length} apartman(a) u sistemu</span>
          </div>
        </div>

        <div className="ar-kpi-card">
          <div className="ar-kpi-label">
            <span className="ic">📊</span>
            Prosjek / noćenje
          </div>
          {Object.keys(avgPerNight).length === 0 ? (
            <div className="ar-kpi-value" style={{ color: "#c4bfef" }}>—</div>
          ) : (
            Object.entries(avgPerNight).map(([currency, avg]) => (
              <div key={currency} className="ar-kpi-value" style={{ fontSize: avg >= 10000 ? 18 : undefined }}>
                {fmtAmount(avg, currency)}
              </div>
            ))
          )}
          <div className="ar-kpi-foot">
            <span>prihod ÷ broj noći u {monthLabel}</span>
          </div>
        </div>
      </div>

      {/* ── Global month navigator ── */}
      <div className="ar-global-period">
        <button className="ar-step" onClick={() => setCurrentDate(prevMonth)}>‹</button>
        <div className="ar-period-val">
          <span className="month">{monthLabel}</span>
          <span className="range">{monthRange}</span>
        </div>
        <button className="ar-step" onClick={() => setCurrentDate(nextMonth)}>›</button>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : (
        <Tabs
          className="ar-tabs"
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />
      )}

      {/* Add / Edit Apartment Modal */}
      <AddApartmentModal
        open={aptModalOpen}
        onClose={() => setAptModalOpen(false)}
        onSaved={(apt) => {
          setAptModalOpen(false);
          loadAll();
          message.success(
            editingApt ? "Apartman ažuriran" : "Apartman dodan"
          );
        }}
        editData={editingApt}
        clientId={clientId}
      />

      {/* Add / Edit Event Modal */}
      <AddEventModal
        open={eventModalOpen}
        onClose={() => setEventModalOpen(false)}
        onSaved={() => {
          setEventModalOpen(false);
          loadAll();
          message.success(editingEvent ? "Događaj ažuriran" : "Događaj dodan");
        }}
        apartments={apartments}
        editData={editingEvent}
        clientId={clientId}
      />

      {/* Add / Edit Reservation Drawer */}
      <AddReservationDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSaved={() => {
          setDrawerOpen(false);
          loadAll();
          message.success(
            editingReservation ? "Rezervacija ažurirana" : "Rezervacija dodana"
          );
        }}
        apartments={apartments}
        editData={editingReservation}
        clientId={clientId}
        defaultApartmentId={defaultAptId}
        prefill={reservationPrefill}
      />

      {/* ── Mobile FAB — Nova rezervacija (hidden on desktop) ── */}
      <button
        className="ar-m-fab"
        onClick={() => openNewReservation()}
        title="Nova rezervacija"
      >
        +
      </button>
    </div>
  );
};

export default ApartmentReservations;
